import React from 'react';
import { EventMediaItem, isLikelyVideoUrl } from '../utils/eventMedia';

interface EventMediaEditorProps {
  mediaItems: EventMediaItem[];
  isUploading?: boolean;
  onUploadImage: (file?: File) => Promise<void> | void;
  onAddVideoUrl: (url: string) => void;
  onMoveItem: (index: number, direction: -1 | 1) => void;
  onRemoveItem: (index: number) => void;
}

export const EventMediaEditor: React.FC<EventMediaEditorProps> = ({
  mediaItems,
  isUploading,
  onUploadImage,
  onAddVideoUrl,
  onMoveItem,
  onRemoveItem,
}) => {
  const [videoUrlDraft, setVideoUrlDraft] = React.useState('');

  const addVideo = () => {
    const normalized = videoUrlDraft.trim();
    if (!normalized) return;
    onAddVideoUrl(normalized);
    setVideoUrlDraft('');
  };

  return (
    <div style={{ display: 'grid', gap: '0.75rem' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem' }}>
        <label
          style={{
            border: '1px solid var(--outline, #555)',
            borderRadius: 8,
            padding: '0.45rem 0.7rem',
            cursor: isUploading ? 'not-allowed' : 'pointer',
            opacity: isUploading ? 0.7 : 1,
          }}
        >
          {isUploading ? 'Uploading image...' : 'Add Image'}
          <input
            type="file"
            accept="image/*"
            onChange={(event) => void onUploadImage(event.target.files?.[0])}
            disabled={isUploading}
            style={{ display: 'none' }}
          />
        </label>
        <input
          type="url"
          value={videoUrlDraft}
          onChange={(event) => setVideoUrlDraft(event.target.value)}
          placeholder="https://youtube.com/watch?v=..."
          style={{ minWidth: 260, flex: '1 1 280px' }}
        />
        <button type="button" onClick={addVideo}>
          Add Video URL
        </button>
      </div>

      {mediaItems.length === 0 ? (
        <p style={{ margin: 0, opacity: 0.8 }}>No media added yet. Add an image and/or video URL.</p>
      ) : (
        <div style={{ display: 'grid', gap: '0.6rem' }}>
          {mediaItems.map((item, index) => {
            const isVideo = item.type === 'video' || isLikelyVideoUrl(item.url);
            return (
              <div
                key={`${item.url}-${index}`}
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'auto 1fr auto',
                  gap: '0.6rem',
                  alignItems: 'center',
                  border: '1px solid var(--outline, #555)',
                  borderRadius: 8,
                  padding: '0.5rem',
                }}
              >
                {isVideo ? (
                  <div
                    style={{
                      width: 72,
                      height: 42,
                      borderRadius: 6,
                      background: 'rgba(0,0,0,0.35)',
                      display: 'grid',
                      placeItems: 'center',
                    }}
                  >
                    <span>Video</span>
                  </div>
                ) : (
                  <img
                    src={item.url}
                    alt="Event media"
                    style={{ width: 72, height: 42, objectFit: 'cover', borderRadius: 6 }}
                  />
                )}
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600 }}>{isVideo ? 'Video' : 'Image'}</div>
                  <div
                    style={{
                      fontSize: '0.82rem',
                      opacity: 0.8,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {item.url}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '0.4rem' }}>
                  <button type="button" onClick={() => onMoveItem(index, -1)} disabled={index === 0}>
                    Up
                  </button>
                  <button
                    type="button"
                    onClick={() => onMoveItem(index, 1)}
                    disabled={index === mediaItems.length - 1}
                  >
                    Down
                  </button>
                  <button type="button" onClick={() => onRemoveItem(index)}>
                    Remove
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
